import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeft, Building, Users, FileText, ChevronDown, ChevronRight, Calendar, Target, Download, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const TaskDetail = () => {
  const { taskNo } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submittedTasks, setSubmittedTasks] = useState([]);
  const [buildingDetails, setBuildingDetails] = useState([]);
  const [trainingDetails, setTrainingDetails] = useState([]);
  const [collapsedSubtasks, setCollapsedSubtasks] = useState({});

  // Helper function to sort subtasks properly (handles numeric and alphanumeric like "1", "2a", "2b", "10", "11")
  const sortSubtasks = (subtasks) => {
    return [...subtasks].sort((a, b) => {
      const aNo = String(a.subtask_no || '');
      const bNo = String(b.subtask_no || '');
      
      // Extract numeric part and letter part (if any)
      const aMatch = aNo.match(/^(\d+)([a-z]?)$/i);
      const bMatch = bNo.match(/^(\d+)([a-z]?)$/i);
      
      if (!aMatch || !bMatch) {
        // Fallback to string comparison if format doesn't match
        return aNo.localeCompare(bNo);
      }
      
      const aNum = parseInt(aMatch[1], 10);
      const bNum = parseInt(bMatch[1], 10);
      const aLetter = aMatch[2] || '';
      const bLetter = bMatch[2] || '';
      
      // First compare by numeric part
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      
      // If numeric parts are equal, compare by letter part
      return aLetter.localeCompare(bLetter);
    });
  };

  const { data: taskData, isLoading } = useQuery(
    ['task-detail', taskNo],
    async () => {
      const response = await axios.get('/api/masters/dropdowns');
      const task = response.data.tasks.find(t => t.task_no === parseInt(taskNo));
      const filteredSubtasks = response.data.subtasks.filter(s => s.task_id === task.task_id);
      const subtasks = sortSubtasks(filteredSubtasks);
      return { task, subtasks };
    }
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load submitted tasks
        const tasksResponse = await axios.get('/api/tasks/submitted');
        const taskTasks = tasksResponse.data.filter(t => t.task_no === parseInt(taskNo));
        setSubmittedTasks(taskTasks);

        // Load building details using existing buildings endpoint
        const buildingResponse = await axios.get('/api/buildings/user');
        setBuildingDetails(buildingResponse.data);

        // Load training details using existing training endpoint
        const trainingResponse = await axios.get('/api/training');
        setTrainingDetails(trainingResponse.data);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    if (taskData?.task?.task_id) {
      loadData();
    }
  }, [taskNo, taskData?.task?.task_id]);

  // Initialize all subtasks as collapsed by default
  useEffect(() => {
    if (taskData?.subtasks) {
      const initialCollapsedState = {};
      taskData.subtasks.forEach(subtask => {
        initialCollapsedState[subtask.subtask_id] = true; // true = collapsed
      });
      setCollapsedSubtasks(initialCollapsedState);
    }
  }, [taskData?.subtasks]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const { task, subtasks } = taskData || {};

  const toggleSubtask = (subtaskId) => {
    setCollapsedSubtasks(prev => ({
      ...prev,
      [subtaskId]: !prev[subtaskId]
    }));
  };

  // Helper function to calculate ISO week number (fallback)
  const calculateISOWeekNumber = (monday, year) => {
    // ISO week 1 is the week containing January 4th
    const jan4 = new Date(year, 0, 4);
    const jan4DayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // If Jan 4 is Sunday (0), Monday is Jan 5. Otherwise, go back (jan4DayOfWeek - 1) days
    const jan4DaysToMonday = jan4DayOfWeek === 0 ? 1 : -(jan4DayOfWeek - 1);
    const week1Monday = new Date(year, 0, 4 + jan4DaysToMonday);
    
    // Calculate week number
    const daysDiff = Math.floor((monday - week1Monday) / (24 * 60 * 60 * 1000));
    let weekNumber = Math.floor(daysDiff / 7) + 1;
    
    // Handle edge case: if the date is in early January but belongs to previous year's last week
    if (weekNumber < 1) {
      const prevYear = year - 1;
      const prevJan4 = new Date(prevYear, 0, 4);
      const prevJan4DayOfWeek = prevJan4.getDay();
      const prevDaysToMonday = prevJan4DayOfWeek === 0 ? 1 : -(prevJan4DayOfWeek - 1);
      const prevWeek1Monday = new Date(prevYear, 0, 4 + prevDaysToMonday);
      const prevDaysDiff = Math.floor((monday - prevWeek1Monday) / (24 * 60 * 60 * 1000));
      weekNumber = Math.floor(prevDaysDiff / 7) + 1;
    }
    
    // Handle edge case: if the date is in late December but belongs to next year's first week
    if (weekNumber > 52) {
      const nextYear = year + 1;
      const nextJan4 = new Date(nextYear, 0, 4);
      const nextJan4DayOfWeek = nextJan4.getDay();
      const nextDaysToMonday = nextJan4DayOfWeek === 0 ? 1 : -(nextJan4DayOfWeek - 1);
      const nextWeek1Monday = new Date(nextYear, 0, 4 + nextDaysToMonday);
      if (monday >= nextWeek1Monday) {
        weekNumber = 1;
      }
    }
    
    return weekNumber;
  };

  // Helper function to get week from date (using cell-specific week numbering)
  const getWeekFromDate = (dateString) => {
    if (!dateString) return 'Select Week';
    
    // Parse date string as local date (YYYY-MM-DD format)
    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) return 'Select Week';
    
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);
    const date = new Date(year, month, day);
    
    if (isNaN(date.getTime())) return 'Select Week';
    
    // Get the date's day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = date.getDay();
    
    // Get Monday of the week
    // If Sunday (0), Monday is next day. Otherwise, go back (dayOfWeek - 1) days
    const daysToMonday = dayOfWeek === 0 ? 1 : -(dayOfWeek - 1);
    const monday = new Date(year, month, day + daysToMonday);
    
    // Get Sunday of the week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    // Calculate week number relative to cell establishment date
    let weekNumber;
    let week1Monday;
    
    if (user?.cellEstablishmentDate) {
      // Use cell establishment date as reference
      const establishmentDate = new Date(user.cellEstablishmentDate);
      if (!isNaN(establishmentDate.getTime())) {
        // Get Monday of the week containing establishment date
        const estDayOfWeek = establishmentDate.getDay();
        const estDaysToMonday = estDayOfWeek === 0 ? 1 : -(estDayOfWeek - 1);
        week1Monday = new Date(establishmentDate);
        week1Monday.setDate(establishmentDate.getDate() + estDaysToMonday);
        // Set to start of day
        week1Monday.setHours(0, 0, 0, 0);
        
        // Calculate week number
        const daysDiff = Math.floor((monday - week1Monday) / (24 * 60 * 60 * 1000));
        weekNumber = Math.floor(daysDiff / 7) + 1;
        
        // Ensure week number is at least 1
        if (weekNumber < 1) {
          weekNumber = 1;
        }
      } else {
        // Fallback to ISO week numbering if establishment date is invalid
        weekNumber = calculateISOWeekNumber(monday, year);
      }
    } else {
      // Fallback to ISO week numbering if no establishment date
      weekNumber = calculateISOWeekNumber(monday, year);
    }
    
    const startDate = monday.toLocaleDateString();
    const endDate = sunday.toLocaleDateString();
    return `Week ${weekNumber} (${startDate} - ${endDate})`;
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'completed') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {status}
        </span>
      );
    } else if (statusLower === 'in progress' || statusLower === 'in-progress') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
          <Clock className="h-3.5 w-3.5" />
          {status}
        </span>
      );
    } else if (statusLower === 'on hold') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
          <AlertCircle className="h-3.5 w-3.5" />
          {status}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
        {status || 'Not Started'}
      </span>
    );
  };

  const renderTaskTable = (subtaskId) => {
    // Filter tasks by subtask if subtaskId is provided
    const subtaskTasks = subtaskId ? submittedTasks.filter(task => task.subtask_id === subtaskId) : submittedTasks;
    
    if (!subtaskTasks.length) {
      return (
        <div className="text-center py-12 px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm font-medium">
            No tasks submitted{subtaskId ? ' for this subtask' : ''} yet.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  Week
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Quarter
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Remarks
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Attachment
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subtaskTasks.map((task, idx) => (
              <tr key={task.id} className={`hover:bg-blue-50/50 transition-colors duration-150 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {getWeekFromDate(task.task_date)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {task.quarter_name}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-700 max-w-md">
                    {task.remarks || <span className="text-gray-400 italic">No remarks</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(task.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {task.document_path ? (
                    <a
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                      href={`${window.location.protocol}//${window.location.hostname}/${task.document_path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download className="h-3.5 w-3.5" />
                      View
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getFilteredBuildings = (taskNo, subtaskNo) => {
    if (!buildingDetails.length) return [];

    // Filter buildings based on task/subtask and building project ID
    let filteredBuildings = [];
    
    if (taskNo === 2 && subtaskNo === 3) {
      // Task 2, Subtask 3: Show buildings where islabelling=false AND isother=false OR projectbuildingid=1 OR projectbuildingid=2
      filteredBuildings = buildingDetails.filter(building => {
        const projectId = String(building.building_project_id);
        const isLabelling = building.islabelling === false;
        const isOther = building.isother === false;
        
        return (isLabelling && isOther) || projectId === '1' || projectId === '2';
      });
    } else if (taskNo === 4 && subtaskNo === 1) {
      // Task 4, Subtask 1: Show buildings where islabelling=true OR projectbuildingid=3,4,5
      filteredBuildings = buildingDetails.filter(building => {
        const projectId = String(building.building_project_id);
        const isLabelling = building.islabelling === true;
        
        return isLabelling || projectId === '3' || projectId === '4' || projectId === '5';
      });
    } else if (taskNo === 5 && subtaskNo === 2) {
      // Task 5, Subtask 2: Show buildings where isother=true OR projectbuildingid=6
      filteredBuildings = buildingDetails.filter(building => {
        const projectId = String(building.building_project_id);
        const isOther = building.isother === true;
        
        return isOther || projectId === '6';
      });
    } else {
      // For other tasks, show no buildings
      filteredBuildings = [];
    }
    
    return filteredBuildings;
  };

  const renderBuildingDetailsTable = (taskNo, subtaskNo) => {
    const filteredBuildings = getFilteredBuildings(taskNo, subtaskNo);
    
    if (!filteredBuildings.length) {
      return (
        <div className="text-center py-12 px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Building className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm font-medium">
            No building details available for this task/subtask combination.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Building Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Address
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Project Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Quarter
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Last Updated
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredBuildings.map((building, idx) => (
              <tr key={building.building_id} className={`hover:bg-blue-50/50 transition-colors duration-150 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{building.building_name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-700 max-w-md">{building.building_address}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-700">{building.project_type || '-'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(building.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {building.quarter_name ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      {building.quarter_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {building.submission_date ? new Date(building.submission_date).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTrainingTable = () => {
    if (!trainingDetails.length) {
      return (
        <div className="text-center py-12 px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Users className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm font-medium">
            No training details available for this task.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Training Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Mode
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Start Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                End Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                District
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Total Attendees
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Govt. Officials
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Female Participants
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                State
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {trainingDetails.map((training, idx) => (
              <tr key={training.training_detail_id} className={`hover:bg-blue-50/50 transition-colors duration-150 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{training.training_type}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {training.training_mode || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {training.training_start_date ? new Date(training.training_start_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {training.training_end_date ? new Date(training.training_end_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {training.district_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    {training.total_attendees || 0}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-50 text-slate-800 border border-slate-200">
                    {training.government_sector_attendees ?? training.Government_Sector_Attendees ?? 0}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-pink-50 text-pink-700 border border-pink-200">
                    {training.total_women || 0}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {training.state_ut || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getTaskSpecificContent = () => {
    switch (parseInt(taskNo)) {
      case 1:
        return (
          <div className="space-y-4">
            {subtasks?.map((subtask, index) => {
              const isExpanded = !collapsedSubtasks[subtask.subtask_id];
              const taskCount = submittedTasks.filter(t => t.subtask_id === subtask.subtask_id).length;
              return (
                <div 
                  key={subtask.subtask_id} 
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-all duration-200"
                    onClick={() => toggleSubtask(subtask.subtask_id)}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
                        <Target className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          Subtask {subtask.subtask_no}: {subtask.subtask_description}
                        </h3>
                        {taskCount > 0 && (
                          <p className="text-sm text-gray-500">
                            {taskCount} {taskCount === 1 ? 'task' : 'tasks'} submitted
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                      </div>
                      {renderTaskTable(subtask.subtask_id)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {subtasks?.map((subtask, index) => {
              const isExpanded = !collapsedSubtasks[subtask.subtask_id];
              const taskCount = submittedTasks.filter(t => t.subtask_id === subtask.subtask_id).length;
              const buildingCount = subtask.subtask_no === 3 ? getFilteredBuildings(2, 3).length : 0;
              return (
                <div 
                  key={subtask.subtask_id} 
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-all duration-200"
                    onClick={() => toggleSubtask(subtask.subtask_id)}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
                        <Target className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          Subtask {subtask.subtask_no}: {subtask.subtask_description}
                        </h3>
                        {(taskCount > 0 || buildingCount > 0) && (
                          <p className="text-sm text-gray-500">
                            {subtask.subtask_no === 3 
                              ? `${buildingCount} ${buildingCount === 1 ? 'building' : 'buildings'} available`
                              : `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} submitted`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                      {subtask.subtask_no === 3 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Building className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Building Details</h4>
                          </div>
                          {renderBuildingDetailsTable(2, 3)}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                          </div>
                          {renderTaskTable(subtask.subtask_id)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {subtasks?.map((subtask, index) => {
              const isExpanded = !collapsedSubtasks[subtask.subtask_id];
              const taskCount = submittedTasks.filter(t => t.subtask_id === subtask.subtask_id).length;
              const trainingCount = subtask.subtask_no === 2 ? trainingDetails.length : 0;
              return (
                <div 
                  key={subtask.subtask_id} 
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-all duration-200"
                    onClick={() => toggleSubtask(subtask.subtask_id)}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
                        <Target className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          Subtask {subtask.subtask_no}: {subtask.subtask_description}
                        </h3>
                        {(taskCount > 0 || trainingCount > 0) && (
                          <p className="text-sm text-gray-500">
                            {subtask.subtask_no === 2 
                              ? `${trainingCount} ${trainingCount === 1 ? 'training' : 'trainings'} conducted`
                              : `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} submitted`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                      {subtask.subtask_no === 2 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Users className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Training Conducted</h4>
                          </div>
                          {renderTrainingTable()}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                          </div>
                          {renderTaskTable(subtask.subtask_id)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            {subtasks?.map((subtask, index) => {
              const isExpanded = !collapsedSubtasks[subtask.subtask_id];
              const taskCount = submittedTasks.filter(t => t.subtask_id === subtask.subtask_id).length;
              const buildingCount = subtask.subtask_no === 1 ? getFilteredBuildings(4, 1).length : 0;
              return (
                <div 
                  key={subtask.subtask_id} 
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-all duration-200"
                    onClick={() => toggleSubtask(subtask.subtask_id)}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
                        <Target className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          Subtask {subtask.subtask_no}: {subtask.subtask_description}
                        </h3>
                        {(taskCount > 0 || buildingCount > 0) && (
                          <p className="text-sm text-gray-500">
                            {subtask.subtask_no === 1 
                              ? `${buildingCount} ${buildingCount === 1 ? 'building' : 'buildings'} available`
                              : `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} submitted`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                      {subtask.subtask_no === 1 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Building className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Building Details</h4>
                          </div>
                          {renderBuildingDetailsTable(4, 1)}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                          </div>
                          {renderTaskTable(subtask.subtask_id)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            {subtasks?.map((subtask, index) => {
              const isExpanded = !collapsedSubtasks[subtask.subtask_id];
              const taskCount = submittedTasks.filter(t => t.subtask_id === subtask.subtask_id).length;
              const buildingCount = subtask.subtask_no === 2 ? getFilteredBuildings(5, 2).length : 0;
              return (
                <div 
                  key={subtask.subtask_id} 
                  className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-all duration-200"
                    onClick={() => toggleSubtask(subtask.subtask_id)}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-100' : 'bg-gray-100'} transition-colors`}>
                        <Target className={`h-5 w-5 ${isExpanded ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          Subtask {subtask.subtask_no}: {subtask.subtask_description}
                        </h3>
                        {(taskCount > 0 || buildingCount > 0) && (
                          <p className="text-sm text-gray-500">
                            {subtask.subtask_no === 2 
                              ? `${buildingCount} ${buildingCount === 1 ? 'building' : 'buildings'} available`
                              : `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} submitted`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                      {subtask.subtask_no === 1 ? (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                          </div>
                          {renderTaskTable(subtask.subtask_id)}
                        </>
                      ) : subtask.subtask_no === 2 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Building className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Building Details</h4>
                          </div>
                          {renderBuildingDetailsTable(5, 2)}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <h4 className="text-md font-semibold text-gray-800">Task Details</h4>
                          </div>
                          {renderTaskTable(subtask.subtask_id)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      default:
        return (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Task Details</h3>
              </div>
            </div>
            <div className="p-5 bg-gray-50/50">
              {renderTaskTable()}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Task {taskNo}: {task?.task_description}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {subtasks?.length || 0} {subtasks?.length === 1 ? 'Subtask' : 'Subtasks'} available
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subtasks Section */}
      <div className="space-y-4">
        {getTaskSpecificContent()}
      </div>
    </div>
  );
};

export default TaskDetail;
